import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { FormattingService } from '../core/formatting.service';

@Injectable({
  providedIn: 'root',
})
export class RouterUtil {
  private _currentModuleName = '<none>';
  get currentModuleName() {
    return this._currentModuleName;
  }
  set currentModuleName(value) {
    // because various components are setting this value in ngOnInit,
    // the ExpressionChangedAfterItHasBeenCheckedError is thrown. Workaround.
    setTimeout(() => {
      this._currentModuleName = this.translate.instant(value ?? '');
    });
  }

  private translate = inject(TranslateService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private formatting = inject(FormattingService);

  extractModuleNameFromRoute(route: ActivatedRoute) {
    const moduleName = this.getDataElementFromRoute(
      route,
      'ui.headerTitle',
      true
    );

    if (moduleName && moduleName !== '') {
      this.currentModuleName = moduleName;
      document.title = 'SHIPM8 - ' + this.translate.instant(moduleName);
    }
  }

  getDataElementFromRoute(
    route: ActivatedRoute,
    itemPath: string,
    iterateChildren = false
  ) {
    let result: any = null;
    if (route.snapshot && route.snapshot.data) {
      const propValue = this.formatting.getPropertyByPath(
        route.snapshot.data,
        itemPath
      );
      if (propValue !== undefined && propValue !== null) {
        result = propValue;
      }
    }

    if (
      route.children &&
      route.children.length > 0 &&
      iterateChildren === true
    ) {
      route.children.forEach((child) => {
        const childValue = this.getDataElementFromRoute(child, itemPath, true);
        if (childValue !== undefined && childValue !== null) {
          result = childValue;
        }
      });
    }

    return result;
  }

  updateQueryParams(params: Params | null) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: params,
      queryParamsHandling: 'merge',
    });
  }

  createParamsFromModelArray = <T>(
    model: new () => T,
    items: T[],
    customKeyMapping?: { [key: string]: string }
  ): {
    params: { [key: string]: string };
    keyMapping: { [key: string]: string };
  } => {
    const modelParamKey = new model().constructor.name[0].toLowerCase();
    const keyMapping: { [key: string]: string } = customKeyMapping || {};
    const params: { [key: string]: string } = {};

    items?.forEach((item, i) => {
      const usedKeys = new Set<string>();
      Object.keys(item).forEach((key) => {
        let uniqueKey: string;
        if (customKeyMapping && customKeyMapping[key]) {
          uniqueKey = customKeyMapping[key];
        } else {
          uniqueKey = this.getParamKey(key, usedKeys);
          keyMapping[`${modelParamKey}${i}_${uniqueKey}`] = key;
        }

        params[`${modelParamKey}${i}_${uniqueKey}`] = item[key];
        usedKeys.add(uniqueKey);
      });
    });

    return { params, keyMapping };
  };

  parseParamsToModelArray = <T>(
    model: new () => T,
    params: { [key: string]: string },
    keyMapping: { [key: string]: string }
  ): T[] => {
    const items: T[] = [];
    const itemMap: { [key: number]: Partial<T> } = {};

    Object.keys(params).forEach((paramKey) => {
      const parts = paramKey.split('_');
      if (parts.length === 2) {
        const i = parseInt(parts[0].slice(1), 10);
        const originalKey = keyMapping[paramKey];

        if (!itemMap[i]) {
          itemMap[i] = {};
        }

        itemMap[i][originalKey] = params[paramKey];
      }
    });

    Object.keys(itemMap).forEach((i) => {
      const item = new model();
      Object.assign(item, itemMap[i]);
      items.push(item);
    });

    return items;
  };

  getParamKey = (key: string, usedKeys: Set<string>): string => {
    let paramKey = key[0].toLowerCase();
    let i = 1;
    while (usedKeys.has(paramKey)) {
      paramKey = key.slice(0, i + 1).toLowerCase();
      i++;
    }

    return paramKey;
  };
}
